import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as firebaseui from 'firebaseui';
import {
    firebaseAuthObj,
    firebaseUiInstance,
    createAuthData,
} from 'lib/firebase';
import 'firebaseui/dist/firebaseui.css';
import { RootState } from 'store';
import { loginSuccessful, StoredUser } from 'store/features/authentication';
import {
    getAuth,
    isSignInWithEmailLink,
    signInWithEmailLink,
    GoogleAuthProvider,
    EmailAuthProvider,
} from 'firebase/auth';
import { ConfirmEmailDialog } from '../ConfirmEmailDialog/ConfirmEmailDialog';
import './styledFirebaseAuth.css';
interface Props {
    uiConfig: firebaseui.auth.Config;
    uiCallback?(ui: firebaseui.auth.AuthUI): void;
    className?: string;
}

const StyledFirebaseAuth = ({ uiConfig, className, uiCallback }: Props) => {
    const elementRef = useRef(null);

    const authentication = useSelector(
        (state: RootState) => state.authentication
    );

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const auth = getAuth();

    const [openConfirmEmailDialog, setOpenConfirmEmailDialog] = useState(false);
    const [userEmail, setUserEmail] = useState<string>('');
    const [emailLoginLink] = useState<string>(window.location.href);

    uiConfig = {
        signInOptions: [
            { provider: GoogleAuthProvider.PROVIDER_ID },
            {
                provider: EmailAuthProvider.PROVIDER_ID,
                signInMethod: EmailAuthProvider.EMAIL_LINK_SIGN_IN_METHOD,
            },
            {
                provider: 'microsoft.com',
                customParameters: {
                    tenant: process.env.REACT_APP_MS_TENANT_ID,
                    prompt: 'select_account',
                },
                scopes: ['email', 'openid', 'profile'],
                // Microsoft requires setting the loginHint parameter dynamically based on the user's input
                loginHintKey: 'login_hint',
            },
        ],
        signInSuccessUrl: '/',
        signInFlow: 'popup',
    };

    // Check if users are logging in via an emailed link.
    useEffect(() => {
        // Verify that the link is actually an email login link.
        if (emailLoginLink && isSignInWithEmailLink(auth, emailLoginLink)) {
            // So, ideally we'd be able to grab the email the user provided on the Landing page and save that in localStorage (as described here: https://firebase.google.com/docs/auth/web/email-link-auth)
            // but I haven't figured out how to interact with that data via the styled firebase auth UI.
            // So in the meantime, a workaround is to prompt the user for the email. Otherwise, we may have to roll our own auth UI.
            setOpenConfirmEmailDialog(true);
        }
    }, [auth, emailLoginLink]);

    // Perform the email link login if the user provides an email.
    useEffect(() => {
        if (userEmail) {
            // If userEmail doesn't match the email associated with the link, the log in will fail.
            signInWithEmailLink(auth, userEmail, emailLoginLink);
        }
    }, [auth, emailLoginLink, userEmail]);

    useEffect(() => {
        if (!firebaseUiInstance || !firebaseAuthObj) return;

        const firebaseUiWidget = firebaseUiInstance;

        if (uiConfig.signInFlow === 'popup') firebaseUiWidget.reset();
        const unregisterIdTokenChangedObserver =
            firebaseAuthObj.onIdTokenChanged(
                (user: any) => {
                    if (!user || !!authentication.user) return;
                    createAuthData(user).then((storedUser: StoredUser) => {
                        dispatch(loginSuccessful(storedUser));
                        navigate('/live-reporting');
                    });
                },
                (error) => {
                    throw Error(`Authentication error: ${error}`);
                }
            );
        if (uiCallback) uiCallback(firebaseUiWidget);

        if (!!elementRef.current)
            firebaseUiWidget.start(elementRef.current, uiConfig);

        return () => {
            unregisterIdTokenChangedObserver();
            firebaseUiWidget.reset();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div
                className={className}
                ref={elementRef}
                data-testid="StyledFirebaseAuth"
            />
            <ConfirmEmailDialog
                modalOpen={openConfirmEmailDialog}
                setModalOpen={setOpenConfirmEmailDialog}
                onSubmitEmail={setUserEmail}
            />
        </>
    );
};

export default StyledFirebaseAuth;
